import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';
import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import Box from 'components/ui/box';
import { useAuth } from 'context/AuthContext';
import { actionLog, registerInteractionCampaign } from 'services/actionlog.service';
import gsap from 'gsap';

import './style.scss';

interface MagicParticle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
  duration: number;
}

export function ChristmasGiftModal() {
  const modalID = 'christmas-gift';

  const [modalIsOpen, setIsOpen] = useState(false);
  const [giftClaimed, setGiftClaimed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [particles, setParticles] = useState<MagicParticle[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const mainBenefitRef = useRef<HTMLDivElement>(null);
  const focusTargetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const { openModal, closeModal } = useModal();
  const { userInfo, refreshUser } = useAuth();

  ReactModal.setAppElement('#modal');

  const handleCloseModal = useCallback(() => {
    // Remove foco antes de fechar para evitar retornar ao botão
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    closeModal();
    refreshUser();
  }, [closeModal, refreshUser]);

  function afterOpenModal() {
    // Salva o elemento que tinha foco antes de abrir a modal
    previousFocusRef.current = document.activeElement as HTMLElement;
    
    sendActionLog();
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 500);

    // Move foco para a modal para que o leitor de tela anuncie
    setTimeout(() => {
      if (focusTargetRef.current) {
        focusTargetRef.current.focus();
      }
    }, 100);

    const particleColors = ['#FFD700', '#FFA500', '#FFED4E', '#FFB347', '#FFDAB9', '#FFF8DC'];
    const newParticles: MagicParticle[] = [];

    for (let i = 0; i < 60; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 2,
        color: particleColors[Math.floor(Math.random() * particleColors.length)],
        delay: Math.random() * 0.8,
        duration: Math.random() * 5 + 4,
      });
    }
    setParticles(newParticles);

    setTimeout(() => {
      if (titleRef.current) {
        const text = '🎄 PRESENTE DE NATAL ATIVADO! 🎁';
        titleRef.current.textContent = '';
        titleRef.current.style.opacity = '1';

        let index = 0;
        const typingSpeed = 60;

        const typeInterval = setInterval(() => {
          if (index < text.length && titleRef.current) {
            titleRef.current.textContent += text[index];
            index++;
          } else {
            clearInterval(typeInterval);
          }
        }, typingSpeed);
      }
    }, 600);

    setTimeout(() => {
      if (mainBenefitRef.current) {
        gsap.fromTo(
          mainBenefitRef.current,
          {
            scale: 0.3,
            rotationY: -180,
            opacity: 0,
            y: 50,
          },
          {
            scale: 1,
            rotationY: 0,
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: 'elastic.out(1, 0.6)',
          },
        );
      }
    }, 1500);
  }

  useEffect(() => {
    setIsOpen(openModal === modalID);
  }, [openModal]);

  // Fechar com ESC mesmo que overlay click esteja desabilitado
  useEffect(() => {
    if (!modalIsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCloseModal, modalIsOpen]);

  function sendActionLog() {
    try {
      registerInteractionCampaign(
        'me',
        'christmas_2025_gift',

        null,
        null,
        null,
        false,
      );
    } catch (error) {
      console.error('Error logging action:', error);
    }
  }

  async function handleClaimReward() {
    if (contentRef.current) {
      gsap.to(contentRef.current, {
        scale: 0.9,
        opacity: 0,
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          setGiftClaimed(true);
          handleCloseModal();
        },
      });
    } else {
      setGiftClaimed(true);
      handleCloseModal();
    }
  }

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        shouldReturnFocusAfterClose={false}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Presente de Natal"
        portalClassName="relative z-[999]"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center justify-start w-screen h-screen p-6 py-12 overflow-y-auto"
        contentElement={() => (
          <Box
            className="w-[90%] lg:w-auto lg:max-w-[600px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="christmas-gift-title"
            aria-describedby="christmas-gift-description"
          >
            <div
              ref={focusTargetRef}
              tabIndex={-1}
              className="outline-none"
            >
              <Button
                color="primary"
                className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={handleCloseModal}
                type="button"
                aria-label="Fechar modal"
              >
                <IoIosClose color="white" size={40} />
              </Button>

            {!giftClaimed ? (
              <>
                {showFlash && <div className="flash-overlay" />}

                <div ref={contentRef} className="christmas-gift-content-new">
                  <div className="magic-particles-container">
                    {particles.map(particle => (
                      <div
                        key={particle.id}
                        className="magic-particle"
                        style={{
                          left: `${particle.x}%`,
                          top: `${particle.y}%`,
                          width: `${particle.size}px`,
                          height: `${particle.size}px`,
                          backgroundColor: particle.color,
                          animationDelay: `${particle.delay}s`,
                          animationDuration: `${particle.duration}s`,
                        }}
                      />
                    ))}
                  </div>

                  <h2 id="christmas-gift-title" ref={titleRef} className="gift-title-new" style={{ opacity: 0 }}>
                    🎄 PRESENTE DE NATAL ATIVADO! 🎁
                  </h2>

                  <div ref={mainBenefitRef} className="main-benefit-container">
                    <div className="benefit-icon-large">✨</div>

                    <div className="benefit-badge-premium">DEZEMBRO 2025</div>

                    {/* <h3 className="benefit-title-main">PONTOS EM DOBRO</h3> */}

                    <p id="christmas-gift-description" className="benefit-description-main">
                      Você ganhou o <span className="highlight-text">DOBRO DE PONTOS</span> para reconhecer seus colegas no mês de dezembro
                    </p>
                  </div>

                  <button className="gift-action-button" onClick={handleClaimReward} type="button">
                    Envie seus reconhecimentos 🚀
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col items-center justify-center space-y-6">
                  <div className="flex items-center justify-center">
                    <img
                      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%2306A77D'/%3E%3Cpath d='M30 50 L45 65 L70 35' stroke='white' stroke-width='6' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"
                      alt="Sucesso"
                      className="w-20 h-20"
                    />
                  </div>

                  <div className="text-center space-y-3">
                    <h2 className="text-2xl font-bold text-yellow">Presente Resgatado!</h2>
                    <p className="text-white text-base">Seus pontos em dobro foram ativados. Aproveite este ciclo especial!</p>
                  </div>

                  <button
                    className="w-full bg-slate-blue hover:bg-blue-dark text-white font-semibold uppercase text-sm py-3 px-6 rounded transition-colors duration-200"
                    onClick={handleCloseModal}
                    type="button"
                  >
                    Fechar
                  </button>
                </div>
              </>
            )}
            </div>
          </Box>
        )}
      />
    </>
  );
}
